import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";

const ESTADOS = ["Pendiente de valuación", "En inspección", "Certificado", "En exhibición", "Vendido", "Descartado"];
const TIPOS = ["Factura original", "Refacturado", "Seminuevo"];
const ESTADO_COLORS = {
  "Pendiente de valuación": "bg-[#F1F3F3] text-[#3E5641]",
  "En inspección": "bg-[#C4674F]/10 text-[#A0553F]",
  "Certificado": "bg-[#A0553F] text-white",
  "En exhibición": "bg-[#3E5641] text-white",
  "Vendido": "bg-white border border-[#3E5641] text-[#3E5641]",
  "Descartado": "bg-red-50 text-red-700",
};

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (estadoFilter !== "all" && it.estado !== estadoFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.marca, it.modelo, it.folio, it.nombre_contacto, String(it.anio)].filter(Boolean).some((v) => String(v).toLowerCase().includes(t));
  }), [items, q, estadoFilter]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar este vehículo del inventario?")) return;
    try {
      await api.delete(`/inventory/${id}`);
      toast.success("Vehículo eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-inventory">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 01</p>
          <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Inventario</h1>
          <p className="mt-1 text-sm text-[#6B7678]">Autos capturados desde el sitio público o cargados manualmente.</p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)} data-testid="inv-new-btn">
          <Plus size={16} /> Nuevo vehículo
        </button>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Buscar por marca, modelo, folio..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="inv-search" />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56" data-testid="inv-filter-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="inv-loading"
        emptyTestId="inv-empty"
        emptyLabel="Sin vehículos aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Folio</th>
                  <th className="px-4 py-3">Vehículo</th>
                  <th className="px-4 py-3">Año</th>
                  <th className="px-4 py-3">Km</th>
                  <th className="px-4 py-3">Precio deseado</th>
                  <th className="px-4 py-3">Rango sugerido</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((it) => (
                  <tr key={it.id} className="hover:bg-[#F1F3F3]/50" data-testid={`inv-row-${it.id}`}>
                    <td className="px-4 py-3 mono text-[#3E5641]">{it.folio}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#3E5641]">{it.marca} {it.modelo}</div>
                      <div className="text-xs text-[#6B7678]">{it.version}</div>
                    </td>
                    <td className="px-4 py-3 mono">{it.anio}</td>
                    <td className="px-4 py-3 mono">{it.kilometraje?.toLocaleString?.() ?? it.kilometraje}</td>
                    <td className="px-4 py-3 mono">${Number(it.precio_deseado || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 mono text-[#6B7678]">{it.rango_precio_sugerido || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[it.estado] || ""}`}>{it.estado}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(it)} className="mr-2 rounded-sm p-1 text-[#3E5641] hover:bg-[#F1F3F3]" aria-label="Editar" data-testid={`inv-edit-${it.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(it.id)} className="rounded-sm p-1 text-red-600 hover:bg-red-50" aria-label="Eliminar" data-testid={`inv-delete-${it.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && (
        <InventoryEditDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {creating && (
        <InventoryCreateDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function InventoryEditDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    estado: item.estado,
    rango_precio_sugerido: item.rango_precio_sugerido || "",
    comision_pactada: item.comision_pactada ?? "",
    resultado_inspeccion: item.resultado_inspeccion || "",
    marca: item.marca, modelo: item.modelo, anio: item.anio, version: item.version,
    kilometraje: item.kilometraje, precio_deseado: item.precio_deseado,
    tipo_documentacion: item.tipo_documentacion, num_duenos: item.num_duenos,
    origen_placas: item.origen_placas, nombre_contacto: item.nombre_contacto,
    telefono_contacto: item.telefono_contacto, correo: item.correo || "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.anio = Number(payload.anio);
      payload.kilometraje = Number(payload.kilometraje);
      payload.precio_deseado = Number(payload.precio_deseado);
      payload.num_duenos = Number(payload.num_duenos);
      payload.comision_pactada = payload.comision_pactada === "" ? null : Number(payload.comision_pactada);
      if (payload.correo === "") payload.correo = null;
      await api.patch(`/inventory/${item.id}`, payload);
      toast.success("Vehículo actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {item.marca} {item.modelo} <span className="mono ml-2 text-sm text-[#6B7678]">{item.folio}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <FieldSel label="Estado" value={form.estado} onChange={(v) => setF("estado", v)} options={ESTADOS} testid="edit-estado" />
          <F label="Rango sugerido (texto)"><Input value={form.rango_precio_sugerido} onChange={(e) => setF("rango_precio_sugerido", e.target.value)} placeholder="Ej. $290,000 – $320,000" data-testid="edit-rango" /></F>
          <F label="% comisión pactada"><Input type="number" step="0.1" value={form.comision_pactada} onChange={(e) => setF("comision_pactada", e.target.value)} data-testid="edit-comision" /></F>
          <FieldSel label="Tipo documentación" value={form.tipo_documentacion} onChange={(v) => setF("tipo_documentacion", v)} options={TIPOS} testid="edit-tipo-doc" />
          <F label="Marca"><Input value={form.marca} onChange={(e) => setF("marca", e.target.value)} /></F>
          <F label="Modelo"><Input value={form.modelo} onChange={(e) => setF("modelo", e.target.value)} /></F>
          <F label="Año"><Input type="number" value={form.anio} onChange={(e) => setF("anio", e.target.value)} /></F>
          <F label="Versión"><Input value={form.version} onChange={(e) => setF("version", e.target.value)} /></F>
          <F label="Kilometraje"><Input type="number" value={form.kilometraje} onChange={(e) => setF("kilometraje", e.target.value)} /></F>
          <F label="Precio deseado"><Input type="number" value={form.precio_deseado} onChange={(e) => setF("precio_deseado", e.target.value)} /></F>
          <F label="Núm. dueños"><Input type="number" value={form.num_duenos} onChange={(e) => setF("num_duenos", e.target.value)} /></F>
          <F label="Origen de placas"><Input value={form.origen_placas} onChange={(e) => setF("origen_placas", e.target.value)} /></F>
          <F label="Nombre contacto"><Input value={form.nombre_contacto} onChange={(e) => setF("nombre_contacto", e.target.value)} /></F>
          <F label="Teléfono"><Input value={form.telefono_contacto} onChange={(e) => setF("telefono_contacto", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
        </div>
        <div>
          <Label className="field-label">Resultado de inspección / notas</Label>
          <Textarea rows={4} value={form.resultado_inspeccion} onChange={(e) => setF("resultado_inspeccion", e.target.value)} data-testid="edit-notas" />
        </div>
        {item.fotos?.length > 0 && (
          <div>
            <Label className="field-label">Fotos capturadas ({item.fotos.length})</Label>
            <div className="grid grid-cols-4 gap-2 md:grid-cols-6">
              {item.fotos.map((p, i) => (
                <img key={p} src={fileUrl(p)} alt="" className="aspect-square w-full border border-[#3E5641]/10 object-cover" />
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark" data-testid="edit-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="edit-save">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InventoryCreateDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    marca: "", modelo: "", anio: "", version: "", kilometraje: "", precio_deseado: "",
    tipo_documentacion: "Factura original", num_duenos: "", origen_placas: "",
    nombre_contacto: "", telefono_contacto: "", correo: "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form, anio: Number(form.anio), kilometraje: Number(form.kilometraje),
        precio_deseado: Number(form.precio_deseado), num_duenos: Number(form.num_duenos),
        fotos: [], // photos are gathered by WhatsApp, not uploaded from admin
        correo: form.correo || null,
      };
      await api.post("/inventory", payload);
      toast.success("Vehículo creado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Nuevo vehículo</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Marca *"><Input required value={form.marca} onChange={(e) => setF("marca", e.target.value)} /></F>
          <F label="Modelo *"><Input required value={form.modelo} onChange={(e) => setF("modelo", e.target.value)} /></F>
          <F label="Año *"><Input type="number" required value={form.anio} onChange={(e) => setF("anio", e.target.value)} /></F>
          <F label="Versión *"><Input required value={form.version} onChange={(e) => setF("version", e.target.value)} /></F>
          <F label="Kilometraje *"><Input type="number" required value={form.kilometraje} onChange={(e) => setF("kilometraje", e.target.value)} /></F>
          <F label="Precio deseado (MXN) *"><Input type="number" required value={form.precio_deseado} onChange={(e) => setF("precio_deseado", e.target.value)} /></F>
          <FieldSel label="Tipo documentación *" value={form.tipo_documentacion} onChange={(v) => setF("tipo_documentacion", v)} options={TIPOS} />
          <F label="Núm. dueños *"><Input type="number" required value={form.num_duenos} onChange={(e) => setF("num_duenos", e.target.value)} /></F>
          <F label="Origen placas *"><Input required value={form.origen_placas} onChange={(e) => setF("origen_placas", e.target.value)} /></F>
          <F label="Nombre contacto *"><Input required value={form.nombre_contacto} onChange={(e) => setF("nombre_contacto", e.target.value)} /></F>
          <F label="Teléfono *"><Input required value={form.telefono_contacto} onChange={(e) => setF("telefono_contacto", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
        </div>
        <p className="text-xs text-[#6B7678]">
          Las fotos del vehículo se solicitan por WhatsApp al consignante y se archivan fuera de la plataforma.
        </p>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="inv-create-save">
            {saving ? "Guardando..." : "Crear"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="field-label">{label}</Label>{children}</div>;
}
function FieldSel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="field-label">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
